import type { MatchState, PlayerState } from '../sim/state';
import type { SimEvent } from '../sim/events';
import type { SimConfig } from '../config/simConfig';
import type { TeamIdx } from '../types';
import { startRestart } from './phase';

// Offside (§6.3, 11v11 toggle). Pure. Evaluated at each attacking-team touch: a teammate beyond the
// second-last defender AND beyond the ball AND in the opponent half at the pass moment is flagged
// `offsideLiable`. A liable player then touching the ball ⇒ FOUL(offside) → FREE_KICK. Liability clears
// on any defender touch, on a fresh onside teammate touch (re-evaluated), and on a dead ball. Simplified
// vs the Laws (no interfering-with-play nuance) — P13.

function clearLiability(state: MatchState): void {
  for (const p of state.players) p.offsideLiable = false;
}

/** Attack-direction projection: higher = closer to the opponent goal for `team`. */
function proj(x: number, team: TeamIdx, state: MatchState): number {
  return state.attackDir[team] * x;
}

/** True if `m` sits beyond the second-last defender, beyond the ball, and in the opponent half (§6.3). */
function inOffsidePosition(m: PlayerState, team: TeamIdx, state: MatchState): boolean {
  const mp = proj(m.pos.x, team, state);
  if (mp <= 0) return false; // own half / halfway — never offside
  if (mp <= proj(state.ball.pos.x, team, state)) return false; // not beyond the ball
  const defTeam: TeamIdx = team === 0 ? 1 : 0;
  const defProjs = state.players
    .filter((p) => p.team === defTeam && !p.sentOff)
    .map((p) => proj(p.pos.x, team, state))
    .sort((a, b) => a - b);
  if (defProjs.length < 2) return false;
  const secondLast = defProjs[defProjs.length - 2]!; // the 2nd-closest defender to their own goal
  return mp > secondLast;
}

/** Handle offside on a touch this tick. Returns true if a liable-player touch produced a FOUL (dead ball). */
export function detectOffside(state: MatchState, events: SimEvent[], cfg: SimConfig): boolean {
  const lt = state.ball.lastTouch;
  if (!lt || lt.tick !== state.tick) return false; // only on a touch this tick
  const toucher = state.players[lt.entity];
  if (!toucher) return false;

  // a liable attacker touching the ball ⇒ offside offence → FREE_KICK to the defenders at the touch spot
  if (toucher.offsideLiable) {
    const defTeam: TeamIdx = toucher.team === 0 ? 1 : 0;
    const spot = { x: state.ball.pos.x, y: state.ball.pos.y };
    events.push({ t: 'FOUL', offender: toucher.id, victim: -1, severity: 0, outcome: 'FREE_KICK', restart: 'FREE_KICK', spot });
    events.push({ t: 'WHISTLE', kind: 'FOUL' });
    clearLiability(state);
    startRestart(state, events, cfg, 'FREE_KICK', defTeam, spot);
    return true;
  }

  // onside touch (incl. a defender winning the ball) ⇒ re-evaluate liability fresh from this pass moment
  clearLiability(state);
  for (const m of state.players) {
    if (m.team === toucher.team && m.id !== toucher.id && !m.sentOff && inOffsidePosition(m, toucher.team, state)) {
      m.offsideLiable = true;
    }
  }
  return false;
}
